import type { DeploymentResponse } from '@emma/api_client/types';
import { Box, Link, Text } from '@lego/klik-ui';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useApplicationVersion } from '@/hooks/application_version';
import { paths } from '@/utility/global_paths';
import { Loader, TextHeader } from '../common';

type Props = {
  deployment: DeploymentResponse;
  workspaceId: number;
};

const DeploymentComposeFileContent = ({
  workspaceId,
  applicationId,
  applicationVersionId,
}: {
  workspaceId: number;
  applicationId: number;
  applicationVersionId: number;
}) => {
  const { data, error, isLoading } = useApplicationVersion(
    workspaceId,
    applicationId,
    applicationVersionId
  );

  if (isLoading || error || !data) {
    return (
      <Box background="white" boxShadow={'md'} padding={6} rounded={'md'} w="full">
        <Text as="h3" fontWeight="medium" isTruncated mb={2} textStyle="h3">
          <Link
            as={ReactRouterLink}
            p={0}
            to={paths.applicationVersionById(workspaceId, applicationId, applicationVersionId)}
          >
            Application Version #{applicationVersionId}
          </Link>
        </Text>
        <Loader />
      </Box>
    );
  }

  const applicationVersion = data;

  return (
    <Box background="white" boxShadow={'md'} padding={6} rounded={'md'} w="full">
      <Text as="h3" fontWeight="medium" isTruncated mb={2} textStyle="h3">
        <Link
          as={ReactRouterLink}
          p={0}
          to={paths.applicationVersionById(workspaceId, applicationId, applicationVersion.id)}
        >
          Application Version #{applicationVersion.id}
        </Link>
      </Text>
      <Text background="#f1f1f1" boxShadow="inner" p={4} whiteSpace="pre-wrap">
        {atob(applicationVersion?.composeFileContents)}
      </Text>
    </Box>
  );
};

export const DeploymentComposeFile = (props: Props): React.JSX.Element => {
  const { applicationId, applicationVersionId } = props.deployment;

  if (applicationId == null || applicationVersionId == null) {
    return (
      <Box background="white" boxShadow={'md'} padding={6} rounded={'md'} w="full">
        <TextHeader mb={2} tagLevel="h3" title="Application Version" />
      </Box>
    );
  }

  return (
    <DeploymentComposeFileContent
      applicationId={applicationId}
      applicationVersionId={applicationVersionId}
      workspaceId={props.workspaceId}
    />
  );
};
